import { useEffect } from 'react';

const isEditableElement = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  if (target.isContentEditable) {
    return true;
  }

  const tagName = target.tagName.toLowerCase();
  return tagName === 'input' || tagName === 'textarea' || tagName === 'select';
};

interface UseTimelineEditorShortcutsParams {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => Promise<void>;
  onRedo: () => Promise<void>;
}

export const useTimelineEditorShortcuts = ({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
}: UseTimelineEditorShortcutsParams): void => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || isEditableElement(event.target) || event.isComposing) {
        return;
      }

      const hasModifierKey = event.metaKey || event.ctrlKey;
      if (!hasModifierKey || event.altKey) {
        return;
      }

      const normalizedKey = event.key.toLowerCase();
      const isUndo = normalizedKey === 'z' && !event.shiftKey;
      const isRedo = (normalizedKey === 'z' && event.shiftKey) || normalizedKey === 'y';

      if (isUndo && canUndo) {
        event.preventDefault();
        void onUndo();
        return;
      }

      if (isRedo && canRedo) {
        event.preventDefault();
        void onRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canRedo, canUndo, onRedo, onUndo]);
};
